import { useSyncExternalStore } from 'react';
import { SETTINGS_KEY, type Settings } from '@/shared/storage/settings';
import {
  DEFAULT_IMAGE_MODEL,
  DEFAULT_LLM_MODEL,
  getImageModel,
  getLlmModel,
} from '@/shared/catalog/models';

const listeners = new Set<() => void>();
let cached: Settings | null = null;

function invalidate(): void {
  cached = null;
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  const onStorage = (e: StorageEvent) => {
    if (e.key === SETTINGS_KEY.key) invalidate();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(cb);
    window.removeEventListener('storage', onStorage);
  };
}

function getSnapshot(): Settings {
  if (cached === null) {
    const raw = SETTINGS_KEY.get();
    cached = {
      ...raw,
      defaultLlmModel: getLlmModel(raw.defaultLlmModel) ? raw.defaultLlmModel : DEFAULT_LLM_MODEL,
      defaultImageModel: getImageModel(raw.defaultImageModel)
        ? raw.defaultImageModel
        : DEFAULT_IMAGE_MODEL,
    };
  }
  return cached;
}

export function useSettings(): readonly [Settings, (patch: Partial<Settings>) => void] {
  const settings = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const update = (patch: Partial<Settings>): void => {
    SETTINGS_KEY.set({ ...SETTINGS_KEY.get(), ...patch });
    invalidate();
  };
  return [settings, update] as const;
}

export function getReplicateToken(): string {
  return SETTINGS_KEY.get().replicateToken;
}
